'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useShippingRate, useDeleteShippingRates } from '@/lib/api/shippingRates'
import { useToast } from '@/components/ui/use-toast'

interface DeleteShippingRateModalProps {
	shippingRateId?: string
	onClose: () => void
	onSuccess?: () => void
}

interface DeleteShippingRateModalContentProps {
	shippingRateId: string
	onClose: () => void
	onSuccess?: () => void
}

export const DeleteShippingRateModal = ({ shippingRateId, onClose, onSuccess }: DeleteShippingRateModalProps) => {
	if (!shippingRateId) return null
	return <DeleteShippingRateModalContent shippingRateId={shippingRateId} onClose={onClose} onSuccess={onSuccess} />
}

const DeleteShippingRateModalContent = ({ shippingRateId, onClose, onSuccess }: DeleteShippingRateModalContentProps) => {
	const { t } = useTranslation('shippings')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const { data: shippingRateData, isPending: isLoadingShippingRate } = useShippingRate(shippingRateId)
	const deleteShippingRates = useDeleteShippingRates()

	const handleDelete = async () => {
		if (!shippingRateId) return

		try {
			await deleteShippingRates.mutateAsync({ ids: [shippingRateId] })

			toast({
				title: tCommon('success'),
				description: t('deleteSuccess'),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			toast({
				title: tCommon('error'),
				description: t('deleteError'),
				variant: 'destructive',
			})
		}
	}

	const shippingRateName = shippingRateData?.shippingRate?.shippingRateName || t('shippingRate')

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.delete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{isLoadingShippingRate
							? t('modals.delete.loading')
							: t('modals.delete.description', { name: shippingRateName })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteShippingRates.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleteShippingRates.isPending || isLoadingShippingRate}
					>
						{deleteShippingRates.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

